import React, {useState, useRef, useContext} from 'react'

import {Box, Button, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import FieldsetForm from '../../../components/shared/ui/FieldsetForm'
import {FormattedMessage, useIntl} from 'react-intl'
import {useToast} from '../../../hooks/use-toast'
import ContactUsFields from '../../../components/forms/contact-us-fields'
import ReCAPTCHA from 'react-google-recaptcha'
import {useContactUs} from '../../../hooks/use-contact-us'
import {IPContext} from '../../../custom-contexts'
import {useXSSProtection} from '../../../hooks/use-xss-protection.js'
import useMultiSite from '../../../hooks/use-multi-site'
import {useHistory} from 'react-router-dom'
import {API_ERROR_MESSAGE, RECAPTCHA_SITE_KEY} from '@app/constants'

/**
 * PDCContactUsForm component renders a contact us form with fields for user input,
 * a reCAPTCHA for verification, and handles form submission.
 *
 * @returns {JSX.Element} The rendered PDCContactUsForm component.
 */

export const PDCContactUsForm = () => {
    const {formatMessage} = useIntl()
    const showToast = useToast()
    const recaptchaRef = useRef()
    const [message, setMessage] = useState('')
    const form = useForm()
    const {handleSubmit, formState, clearErrors} = form

    const [formSubmitted, setFormSubmitted] = useState(false)

    const contactUsHook = useContactUs()
    const {isIPBlocked} = useContext(IPContext)
    const {validateXssContent} = useXSSProtection()
    const {buildUrl} = useMultiSite()
    const history = useHistory()

    /**
     * Handles the form submission, sending the data to the contact us API.
     *
     * @param {Object} data - The form data to be submitted.
     */
    const handleContactUsSubmit = async (data) => {
        if (isIPBlocked) {
            const path = buildUrl('/')
            history.push(path)
            return
        }

        if (!data) {
            return
        }

        try {
            const captchaValue = await recaptchaRef.current.executeAsync()
            const payload = {
                ...data,
                message: message,
                captchaCode: captchaValue
            }

            const isXssDetected = await validateXssContent(message, 'contactus-form', 'message')
            if (isXssDetected) {
                showToast({
                    title: formatMessage({
                        defaultMessage: 'Web links of any sort are refused',
                        id: 'use_form_fields.error.xss_detected'
                    }),
                    status: 'error'
                })
                return
            }

            const result = await contactUsHook.submitContactUs(payload)

            if (result.success) {
                setFormSubmitted(true)
                window.scrollTo({top: 0, behavior: 'smooth'})
            } else {
                showToast({
                    title: result.msg || formatMessage(API_ERROR_MESSAGE),
                    description: result.details || '',
                    status: 'error'
                })
            }
        } catch (error) {
            showToast({
                title: formatMessage(API_ERROR_MESSAGE),
                description: error.message || '',
                status: 'error'
            })
        }
    }

    /**
     * Handles the button click event by clearing global form errors.
     */
    const handleButtonClick = () => {
        clearErrors('global')
    }

    const reCaptchaContainerStyle = {
        '.grecaptcha-badge': {
            position: 'absolute !important',
            bottom: {base: '0 !important', lg: '25px !important'}
        }
    }

    return (
        <Box position={'relative'} width={'100%'} pb={{base: '60px', lg: 0}} overflow={'hidden'}>
            <Box
                mt={{base: '44px', lg: '34px'}}
                px={{base: '10px', lg: '40px'}}
                maxW={{base: 'unset', lg: '920px'}}
            >
                {!formSubmitted ? (
                    <>
                        <Text variant={'bodyBase2'} maxW={{base: 'unset', lg: '756px'}} mb={'16px'}>
                            <FormattedMessage
                                defaultMessage="Due to a very large number of requests, there may be a delay in our usual response time."
                                id="contact_us.advertising"
                            />
                        </Text>
                        <FieldsetForm
                            id="contact-us-form"
                            onSubmit={handleSubmit(handleContactUsSubmit)}
                            error={formState.errors?.global?.message}
                            legendText={formatMessage({
                                defaultMessage: 'Contact us form',
                                id: 'contact_us.form.legend'
                            })}
                        >
                            <ContactUsFields
                                form={form}
                                submitForm={handleContactUsSubmit}
                                message={message}
                                setMessage={setMessage}
                            />
                            <Button
                                type="submit"
                                onClick={handleButtonClick}
                                isLoading={formState.isSubmitting}
                                variant="primary"
                            >
                                <FormattedMessage defaultMessage="Submit" id="contact_us.submit" />
                            </Button>
                            <Box sx={reCaptchaContainerStyle}>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={RECAPTCHA_SITE_KEY}
                                    theme="dark"
                                    size="invisible"
                                />
                            </Box>
                        </FieldsetForm>
                    </>
                ) : (
                    <Text variant={'bodyBase2'} maxW={{base: 'unset', lg: '756px'}} mb={'16px'}>
                        <FormattedMessage
                            defaultMessage="Thank you for contacting Vilebrequin. We will get back to you as soon as possible."
                            id="contact_us.thanks"
                        />
                    </Text>
                )}
            </Box>
        </Box>
    )
}
